import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAdminApiCallMetrics, getAdminAttention, listAdminFailedScheduledTasks, listAdminOrganizations } from '../../admin/adminApi'
import type { AdminAttentionItem, AdminFailedScheduledTask, AdminOrganizationSummary } from '../../admin/types'
import { Button, Card, Icon, I, Pill, gridColumns } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { EmptyState, ErrorState, LoadingState } from './adminPageHelpers'
import { formatDateTime, numberLabel, severityTone } from './adminPageFormatters'
import type { AdminNavPage } from '../../components/admin/AdminShell'

interface AdminHomePageProps {
  accessToken: string
  onNavigate: (page: AdminNavPage) => void
}

interface HomeState {
  organizations: AdminOrganizationSummary[]
  attention: AdminAttentionItem[]
  failedTasks: AdminFailedScheduledTask[]
  todayCalls: number
}

export function AdminHomePage({ accessToken, onNavigate }: AdminHomePageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [state, setState] = useState<HomeState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = useCallback(function load() {
    setLoading(true)
    setError(false)
    Promise.all([
      listAdminOrganizations(accessToken),
      getAdminAttention(accessToken),
      listAdminFailedScheduledTasks(accessToken),
      getAdminApiCallMetrics(accessToken),
    ])
      .then(([organizations, attention, failedTasks, metrics]) => {
        setState({
          organizations,
          attention: attention.items,
          failedTasks,
          todayCalls: metrics.calls.reduce((sum, row) => sum + row.callCount, 0),
        })
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [accessToken])

  useEffect(() => {
    void Promise.resolve().then(load)
  }, [load])

  const stats = useMemo(() => {
    const organizations = state?.organizations ?? []
    return [
      { key: 'organizations', label: t('admin.home.stats.organizations'), value: organizations.length },
      { key: 'users', label: t('admin.home.stats.users'), value: organizations.reduce((sum, org) => sum + org.userCount, 0) },
      { key: 'connections', label: t('admin.home.stats.connections'), value: organizations.reduce((sum, org) => sum + org.channelConnectionCount + org.inventoryConnectionCount, 0) },
      { key: 'failedTasks', label: t('admin.home.stats.failedTasks'), value: state?.failedTasks.length ?? 0 },
      { key: 'apiCalls', label: t('admin.home.stats.apiCalls'), value: state?.todayCalls ?? 0 },
    ]
  }, [state, t])

  if (loading) return <LoadingState label={t('admin.home.loading')} />
  if (error || !state) return <ErrorState title={t('admin.home.error.title')} body={t('admin.home.error.body')} onRetry={load} />

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Card style={{ background: 'linear-gradient(135deg, var(--brand-900), var(--brand-700))', color: 'white', border: 'none' }}>
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 16, alignItems: 'center' }}>
          <div>
            <Pill tone="coral" size="sm">{t('admin.home.warning.pill')}</Pill>
            <h2 style={{ margin: '12px 0 6px', fontSize: 26, lineHeight: 1.05 }}>{t('admin.home.warning.title')}</h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.78)', maxWidth: 740 }}>{t('admin.home.warning.body')}</p>
          </div>
          <Button kind="onDark" onClick={() => onNavigate('admin-organizations')}>{t('admin.home.warning.cta')}</Button>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(5, minmax(0, 1fr))'), gap: 12 }}>
        {stats.map(stat => (
          <Card key={stat.key} padding={18}>
            <div style={{ color: 'var(--fg-subtle)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{stat.label}</div>
            <div style={{ marginTop: 8, fontSize: 28, lineHeight: 1, fontWeight: 900, color: 'var(--brand-900)' }}>{numberLabel(stat.value, i18n.language)}</div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1.35fr) minmax(280px, 0.65fr)'), gap: 18 }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, color: 'var(--brand-900)' }}>{t('admin.home.attention.title')}</h2>
              <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{t('admin.home.attention.subtitle')}</div>
            </div>
            <Button kind="secondary" size="sm" onClick={load} icon={I.refresh}>{t('admin.common.refresh')}</Button>
          </div>
          {state.attention.length === 0 ? (
            <EmptyState title={t('admin.home.attention.emptyTitle')} body={t('admin.home.attention.emptyBody')} />
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {state.attention.map(item => (
                <div key={item.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 14, padding: 14, display: 'grid', gap: 7 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Pill tone={severityTone(item.severity)} size="sm" dot>{t(`admin.severity.${item.severity}`, { defaultValue: item.severity })}</Pill>
                    {item.provider && <Pill tone="info" size="sm">{item.provider}</Pill>}
                    {item.count != null && <span style={{ fontSize: 12, color: 'var(--fg-subtle)', fontWeight: 700 }}>{t('admin.home.attention.count', { count: item.count })}</span>}
                  </div>
                  <div style={{ fontWeight: 900, color: 'var(--fg)' }}>{t(`admin.attention.title.${item.titleCode}`, { defaultValue: item.titleCode })}</div>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{t(`admin.attention.detail.${item.detailCode}`, { defaultValue: item.detailCode })}</div>
                  <div style={{ color: 'var(--fg-subtle)', fontSize: 12 }}>
                    {item.organizationName ?? t('admin.common.platform')} · {formatDateTime(item.occurredAt, i18n.language, t('admin.common.none'))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h2 style={{ margin: 0, fontSize: 20, color: 'var(--brand-900)' }}>{t('admin.home.tools.title')}</h2>
          <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
            {[
              ['admin-organizations', I.building, t('admin.home.tools.organizations')],
              ['admin-api-calls', I.trend, t('admin.home.tools.apiCalls')],
              ['admin-scheduled-tasks', I.clock, t('admin.home.tools.scheduledTasks')],
              ['admin-provider-proxy', I.zap, t('admin.home.tools.providerProxy')],
            ].map(([page, icon, label]) => (
              <button key={page as string} type="button" onClick={() => onNavigate(page as AdminNavPage)} style={{ border: '1px solid var(--border-subtle)', background: 'white', borderRadius: 12, padding: 12, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--brand-800)', fontFamily: 'inherit', fontWeight: 800, cursor: 'pointer', textAlign: 'start' }}>
                <Icon size={16}>{icon}</Icon>
                {label as string}
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
